import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Stack, Typography, Button, TextField, Chip, Avatar, IconButton, Tooltip,
    CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from '@mui/material';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import http, { apiErrorMessage } from '../../Api/http';
import { GetPayslipRequests, UpdatePayslipRequestAction, ApproveAllPayslipRequests } from '../../Api/Api';
import { card, solidBtn, ghostBtn, field, EmptyState } from '../uiKit';
import { initialsFromName as initials, avatarTone } from '../../utils/format';

const monthLabel = (v) => {
    if (!v) return '';
    const s = String(v);
    const m = /^(\d{4})-(\d{2})$/.test(s) ? { y: s.slice(0, 4), mo: s.slice(5, 7) }
        : /^(\d{2})-(\d{4})$/.test(s) ? { y: s.slice(3, 7), mo: s.slice(0, 2) } : null;
    if (!m) return s;
    return new Date(Number(m.y), Number(m.mo) - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

const STATUS_STYLE = {
    pending: { label: 'Pending', color: '#B45309', bg: '#FFF7ED' },
    approved: { label: 'Approved', color: '#16A34A', bg: '#DCFCE7' },
    rejected: { label: 'Rejected', color: '#E11D48', bg: '#FEE2E2' },
};
const StatusPill = ({ status }) => {
    const s = STATUS_STYLE[String(status || '').toLowerCase()] || { label: status || '—', color: '#64748B', bg: '#F1F5F9' };
    return <Chip label={s.label} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: s.bg, color: s.color }} />;
};

export default function PayslipRequestsApproval() {
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    // The list is fetched by the server, so wait for a pause in typing instead of calling it on every key.
    const [query, setQuery] = useState('');
    useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 400); return () => clearTimeout(t); }, [search]);
    const [busyId, setBusyId] = useState(null);
    const [approveAllBusy, setApproveAllBusy] = useState(false);
    const [reject, setReject] = useState(null);
    const [snack, setSnack] = useState(null);
    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetPayslipRequests, {
                params: { status: statusFilter || undefined, search: query || undefined },
            });
            if (body?.error) throw new Error(body.message || 'Could not load payslip requests.');
            const d = body?.data ?? {};
            const list = Array.isArray(d) ? d : (d.requests || d.items || d.list || d.data || []);
            setRows(list.map((r) => ({
                id: r.requestId ?? r.id ?? r.Id,
                employeeCode: r.employeeCode ?? r.EmployeeCode ?? '',
                employeeName: r.employeeName ?? r.EmployeeName ?? r.name ?? '',
                month: r.month ?? r.requestedMonth ?? r.payoutMonth ?? r.fromMonth ?? '',
                note: r.note ?? r.Note ?? '',
                status: r.status ?? r.Status ?? 'Pending',
                reviewedBy: r.reviewedByName ?? r.reviewedBy ?? '',
                reviewedOn: r.reviewedOn ?? '',
                rejectReason: r.rejectReason ?? r.reason ?? '',
            })));
            const c = d.counts ?? d.summary ?? d;
            setCounts({
                total: Number(c.total ?? list.length) || 0,
                pending: Number(c.pending ?? 0) || 0,
                approved: Number(c.approved ?? 0) || 0,
                rejected: Number(c.rejected ?? 0) || 0,
            });
            setLoadError('');
        } catch (err) {
            if (err?.response?.status === 404) { setRows([]); setCounts({ total: 0, pending: 0, approved: 0, rejected: 0 }); setLoadError(''); }
            else { setRows([]); setLoadError(apiErrorMessage(err, 'Could not load payslip requests.')); }
        } finally {
            setLoading(false);
        }
    }, [statusFilter, query]);

    useEffect(() => { load(); }, [load]);

    const cards = useMemo(() => {
        if (counts.pending || counts.approved || counts.rejected || counts.total) return counts;
        const by = (s) => rows.filter((r) => String(r.status).toLowerCase() === s).length;
        return { total: rows.length, pending: by('pending'), approved: by('approved'), rejected: by('rejected') };
    }, [counts, rows]);

    const act = async (row, action, reason = '') => {
        setBusyId(row.id);
        try {
            const { data: body } = await http.put(UpdatePayslipRequestAction, { RequestId: row.id, Action: action, Reason: reason });
            if (body?.error) throw new Error(body.message || 'Could not update the request.');
            notify(action === 'approve' ? `Approved ${monthLabel(row.month)} for ${row.employeeName || row.employeeCode}.` : 'Request rejected.');
            await load();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not update the request.'), 'error');
        } finally { setBusyId(null); }
    };

    const submitReject = async () => {
        const { row, reason } = reject;
        if ((reason || '').trim().length < 3) { notify('Enter a reason (at least 3 characters).', 'warning'); return; }
        setReject(null);
        await act(row, 'reject', reason.trim());
    };

    const approveAll = async () => {
        setApproveAllBusy(true);
        try {
            const { data: body } = await http.post(ApproveAllPayslipRequests, { RequestIds: [] });
            if (body?.error) throw new Error(body.message || 'Could not approve all.');
            notify('Approved all pending payslip requests.');
            await load();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not approve all.'), 'error');
        } finally { setApproveAllBusy(false); }
    };

    const FILTERS = ['', 'Pending', 'Approved', 'Rejected'];

    return (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
            {/* One toolbar row: status filters first, then search, actions at the right. */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                {FILTERS.map((f) => {
                    const on = statusFilter === f;
                    return (
                        <Chip
                            key={f || 'all'} label={f || 'All'} onClick={() => setStatusFilter(f)}
                            sx={{
                                height: 30, fontSize: 12.5, fontWeight: 700, borderRadius: '8px', cursor: 'pointer',
                                bgcolor: on ? '#111827' : '#fff', color: on ? '#fff' : '#475569',
                                border: '1px solid', borderColor: on ? '#111827' : '#E5E7EB',
                                '&:hover': { bgcolor: on ? '#111827' : '#F8FAFC' },
                            }}
                        />
                    );
                })}
                </Stack>
                <TextField
                    size="small" placeholder="Search name / code"
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    sx={{ ...field, width: { xs: '100%', sm: 420 }, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 40 } }}
                    InputProps={{ endAdornment: <InputAdornment position="end"><SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} /></InputAdornment> }}
                />
                <Box sx={{ ml: { sm: 'auto' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip arrow title="Reload"><span>
                        <IconButton onClick={load} sx={{ ...ghostBtn, width: 40, height: 40 }}><RefreshRoundedIcon sx={{ fontSize: 19 }} /></IconButton>
                    </span></Tooltip>
                    <Button
                        onClick={approveAll} disabled={approveAllBusy || cards.pending === 0}
                        startIcon={approveAllBusy ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <DoneAllRoundedIcon sx={{ fontSize: 18 }} />}
                        sx={{ ...solidBtn, height: 40, px: 2 }}
                    >
                        Approve all pending
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={26} /></Box>
            ) : loadError ? (
                <Box sx={{ ...card, p: 0 }}><EmptyState icon={ReceiptLongRoundedIcon} title="Couldn't load requests" hint={loadError} /></Box>
            ) : rows.length === 0 ? (
                <Box sx={{ ...card, p: 0 }}><EmptyState icon={ReceiptLongRoundedIcon} title="No payslip requests" hint="When staff request a payslip for a month, it appears here for approval." /></Box>
            ) : (
                <Stack spacing={1}>
                    {rows.map((r) => {
                        const pending = String(r.status).toLowerCase() === 'pending';
                        const busy = busyId === r.id;
                        return (
                            <Box key={r.id} sx={{ ...card, p: 1.6 }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4} sx={{ alignItems: { md: 'center' } }}>
                                    <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', minWidth: 0, flex: 1 }}>
                                        <Avatar sx={{ width: 40, height: 40, ...avatarTone(r.employeeName || r.employeeCode), fontSize: 14, fontWeight: 700 }}>
                                            {initials(r.employeeName || r.employeeCode)}
                                        </Avatar>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
                                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#111827' }} noWrap>{r.employeeName || r.employeeCode}</Typography>
                                                <StatusPill status={r.status} />
                                            </Stack>
                                            <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }} noWrap>{r.employeeCode}</Typography>
                                        </Box>
                                    </Stack>

                                    <Box sx={{ flexShrink: 0, minWidth: 96 }}>
                                        <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: '#98A0AE', textTransform: 'uppercase', letterSpacing: 0.4 }}>Month</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#374151' }}>{monthLabel(r.month) || '—'}</Typography>
                                    </Box>

                                    {pending && (
                                        <Stack direction="row" spacing={0.8} sx={{ flexShrink: 0 }}>
                                            {busy && <CircularProgress size={18} sx={{ mr: 0.5 }} />}
                                            <Button size="small" disabled={busy} startIcon={<CheckRoundedIcon sx={{ fontSize: 16 }} />} onClick={() => act(r, 'approve')} sx={{ ...solidBtn, height: 32, fontSize: 12.5, bgcolor: '#16A34A', color: '#fff', border: 'none', '& .MuiButton-startIcon': { color: '#fff' }, '&:hover': { bgcolor: '#15803D' } }}>Approve</Button>
                                            <Button size="small" disabled={busy} startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />} onClick={() => setReject({ row: r, reason: '' })} sx={{ ...ghostBtn, height: 32, fontSize: 12.5, color: '#DC2626', borderColor: '#FECACA', '&:hover': { bgcolor: '#FEF2F2', borderColor: '#FCA5A5' } }}>Reject</Button>
                                        </Stack>
                                    )}
                                    {!pending && (r.reviewedBy || r.reviewedOn) && (
                                        <Typography sx={{ fontSize: 11, color: '#98A0AE', flexShrink: 0 }}>{[r.reviewedBy, r.reviewedOn].filter(Boolean).join(' · ')}</Typography>
                                    )}
                                </Stack>

                                {(r.note || r.rejectReason) && (
                                    <Box sx={{ mt: 1.2, pt: 1.2, borderTop: '1px dashed #EEF0F5' }}>
                                        {r.note && <Typography sx={{ fontSize: 12, color: '#475569' }}><Box component="span" sx={{ fontWeight: 700 }}>Note:</Box> {r.note}</Typography>}
                                        {r.rejectReason && <Typography sx={{ fontSize: 12, color: '#B91C1C', mt: r.note ? 0.4 : 0 }}><Box component="span" sx={{ fontWeight: 700 }}>Rejected:</Box> {r.rejectReason}</Typography>}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            )}

            <Dialog open={!!reject} onClose={() => setReject(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                {reject && (
                    <>
                        <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>Reject payslip request</DialogTitle>
                        <DialogContent>
                            <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 1.5 }}>
                                {monthLabel(reject.row.month)} for {reject.row.employeeName || reject.row.employeeCode}. The employee sees this reason and can request again.
                            </Typography>
                            <TextField
                                label="Reason for rejection" size="small" fullWidth multiline minRows={2}
                                value={reject.reason} onChange={(e) => setReject((s) => ({ ...s, reason: e.target.value }))}
                                sx={field} autoFocus
                            />
                        </DialogContent>
                        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
                            <Button onClick={() => setReject(null)} sx={{ ...ghostBtn, height: 40 }}>Cancel</Button>
                            <Button onClick={submitReject} sx={{ ...solidBtn, height: 40, bgcolor: '#DC2626', color: '#fff', border: 'none', '&:hover': { bgcolor: '#B91C1C' } }}>Reject</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                {snack ? <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)} sx={{ borderRadius: '8px' }}>{snack.msg}</Alert> : undefined}
            </Snackbar>
        </Box>
    );
}
